import React, { useState, useEffect, useCallback } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { usePosts } from '@/Recoil/Social';
import './index.scss';

const ImageViewer: React.FC = () => {
    const { posts } = usePosts();
    
    // Parse URL to get postId and imageIndex
    const path = window.location.pathname;
    const match = path.match(/^\/social\/image\/([^/]+)\/(\d+)$/);
    const postId = match?.[1] || '';
    const initialIndex = parseInt(match?.[2] || '0', 10);
    
    const post = posts[postId];
    const media = post?.media || [];
    
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    
    useEffect(() => {
        // Update URL when index changes
        if (currentIndex !== initialIndex && postId) {
            window.history.replaceState(null, '', `/social/image/${postId}/${currentIndex}`);
        }
    }, [currentIndex, initialIndex, postId]);
    
    const handleClose = useCallback(() => {
        window.history.back();
    }, []);
    
    const handlePrevious = useCallback(() => {
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
        }
    }, [currentIndex]);
    
    const handleNext = useCallback(() => {
        if (currentIndex < media.length - 1) {
            setCurrentIndex(currentIndex + 1);
        }
    }, [currentIndex, media.length]);
    
    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                handleClose();
            } else if (e.key === 'ArrowLeft') {
                handlePrevious();
            } else if (e.key === 'ArrowRight') {
                handleNext();
            }
        };
        
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleClose, handlePrevious, handleNext]);
    
    if (!post || media.length === 0) {
        return (
            <div className="ImageViewer">
                <div className="EmptyState">
                    <p>Image not found</p>
                    <button onClick={handleClose}>Go back</button>
                </div>
            </div>
        );
    }
    
    const currentImage = media[currentIndex];
    
    return (
        <div className="ImageViewer">
            {/* Header */}
            <div className="Header">
                <button className="CloseButton" onClick={handleClose}>
                    <CloseIcon />
                </button>
                {media.length > 1 && (
                    <div className="Counter">
                        {currentIndex + 1} / {media.length}
                    </div>
                )}
            </div>
            
            {/* Image Container */}
            <div className="ImageContainer">
                {/* Previous Button */}
                {currentIndex > 0 && (
                    <button className="NavButton Prev" onClick={handlePrevious}>
                        <ChevronLeftIcon />
                    </button>
                )}
                
                {/* Image */}
                <img 
                    src={currentImage} 
                    alt="" 
                    className="Image"
                />
                
                {/* Next Button */}
                {currentIndex < media.length - 1 && (
                    <button className="NavButton Next" onClick={handleNext}>
                        <ChevronRightIcon />
                    </button>
                )}
            </div>
            
            {/* Thumbnail Strip */}
            {media.length > 1 && (
                <div className="ThumbnailStrip">
                    {media.map((url, index) => (
                        <button
                            key={index}
                            className={`Thumbnail ${index === currentIndex ? 'Active' : ''}`}
                            onClick={() => setCurrentIndex(index)}
                        >
                            <img src={url} alt="" />
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ImageViewer;